package org.example.project.enginekalender

import androidx.compose.runtime.Composable
import org.example.project.dashboard.DashboardTheme
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val KALENDER_MINI_CELL_SIZE = 36
private const val KALENDER_MINI_GAP = 6
private const val KALENDER_MINI_WEEKDAY_HEIGHT = 18
private const val KALENDER_MINI_OUTER_WIDTH = 7 * KALENDER_MINI_CELL_SIZE + 6 * KALENDER_MINI_GAP

@Composable
fun KalenderMiniContainer(content: @Composable () -> Unit) {
    Div({
        style {
            width(100.percent)
            maxWidth(100.percent)
            padding(12.px, 6.px)
            property("background", DashboardTheme.PREMIUM_SURFACE_BG)
            property("border", "1px solid rgba(255,255,255,0.10)")
            borderRadius(18.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(10.px)
            boxSizing("border-box")
        }
    }) {
        content()
    }
}

@Composable
fun KalenderMiniGrid(
    monthData: All,
    selectedDate: String,
    onSelectDate: (String) -> Unit,
    dayStatesByDate: Map<String, KalenderDayState> = emptyMap(),
    showWeekBorder: Boolean,
    showDayBorder: Boolean,
    showSelectedDayBorder: Boolean = true,
    cellSizePx: Int = KALENDER_MINI_CELL_SIZE,
    gapPx: Int = KALENDER_MINI_GAP,
    weekdayHeightPx: Int = KALENDER_MINI_WEEKDAY_HEIGHT,
    fontSizePx: Int = 12,
    borderRadiusPx: Int = 12
) {
    val today = parseKalenderIsoDate(kalenderTodayIsoDate())!!
    val outerWidth = 7 * cellSizePx + 6 * gapPx
    val weekOverlayTopPx = monthData.stufe.takeIf { it >= 0 }?.let {
        cellSizePx + gapPx + (it * (cellSizePx + gapPx))
    }

    Div({
        style {
            width(outerWidth.px)
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(7, ${cellSizePx}px)")
            property("grid-auto-rows", "${cellSizePx}px")
            justifyContent(JustifyContent.Center)
            gap(gapPx.px)
            position(Position.Relative)
        }
    }) {
        listOf(
            "calendar_weekday_monday_short" to "Mo",
            "calendar_weekday_tuesday_short" to "Di",
            "calendar_weekday_wednesday_short" to "Mi",
            "calendar_weekday_thursday_short" to "Do",
            "calendar_weekday_friday_short" to "Fr",
            "calendar_weekday_saturday_short" to "Sa",
            "calendar_weekday_sunday_short" to "So"
        ).forEach { (weekdayKey, fallback) ->
            Div({
                style {
                    width(cellSizePx.px)
                    height(weekdayHeightPx.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    textAlign("center")
                    color(Color("#9aa4b5"))
                    fontSize((fontSizePx - 2).coerceAtLeast(10).px)
                    fontWeight("600")
                }
            }) { Text(kalenderLocalized(weekdayKey, fallback)) }
        }
        if (showWeekBorder) {
            weekOverlayTopPx?.let { topPx ->
                Div({
                    style {
                        position(Position.Absolute)
                        left(0.px)
                        top(topPx.px)
                        width(outerWidth.px)
                        height(cellSizePx.px)
                        borderRadius((borderRadiusPx + 2).px)
                        property("border", "1px solid rgba(255,255,255,0.18)")
                        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.04)")
                        property("background", "rgba(255,255,255,0.015)")
                        property("pointer-events", "none")
                        property("z-index", "0")
                    }
                })
            }
        }
        monthData.module.forEachIndexed { index, cell ->
            val hasBooking = cell.aktiv && dayStatesByDate.containsKey(cell.datum)
            KalenderDayField(
                label = cell.label,
                isCurrentMonth = cell.aktiv,
                isToday = parseKalenderIsoDate(cell.datum) == today,
                hasDayBorder = showDayBorder && showSelectedDayBorder && cell.datum == selectedDate,
                cellSizePx = cellSizePx,
                fontSizePx = fontSizePx,
                borderRadiusPx = borderRadiusPx,
                hasBooking = hasBooking,
                bookingColor = Color("rgba(255,255,255,0.72)"),
                onClick = { onSelectDate(cell.datum) }
            )
        }
    }
}

@Composable
fun KalenderMiniWeekBorderLayout(
    monthData: All,
    selectedDate: String,
    onSelectDate: (String) -> Unit,
    dayStatesByDate: Map<String, KalenderDayState> = emptyMap(),
    showSelectedDayBorder: Boolean = true,
    cellSizePx: Int = KALENDER_MINI_CELL_SIZE,
    gapPx: Int = KALENDER_MINI_GAP,
    weekdayHeightPx: Int = KALENDER_MINI_WEEKDAY_HEIGHT,
    fontSizePx: Int = 12,
    borderRadiusPx: Int = 12
) {
    KalenderMiniGrid(
        monthData = monthData,
        selectedDate = selectedDate,
        onSelectDate = onSelectDate,
        dayStatesByDate = dayStatesByDate,
        showWeekBorder = true,
        showDayBorder = false,
        showSelectedDayBorder = showSelectedDayBorder,
        cellSizePx = cellSizePx,
        gapPx = gapPx,
        weekdayHeightPx = weekdayHeightPx,
        fontSizePx = fontSizePx,
        borderRadiusPx = borderRadiusPx
    )
}

@Composable
fun KalenderMiniDayBorderLayout(
    monthData: All,
    selectedDate: String,
    onSelectDate: (String) -> Unit,
    dayStatesByDate: Map<String, KalenderDayState> = emptyMap(),
    showSelectedDayBorder: Boolean = true,
    cellSizePx: Int = KALENDER_MINI_CELL_SIZE,
    gapPx: Int = KALENDER_MINI_GAP,
    weekdayHeightPx: Int = KALENDER_MINI_WEEKDAY_HEIGHT,
    fontSizePx: Int = 12,
    borderRadiusPx: Int = 12
) {
    KalenderMiniGrid(
        monthData = monthData,
        selectedDate = selectedDate,
        onSelectDate = onSelectDate,
        dayStatesByDate = dayStatesByDate,
        showWeekBorder = false,
        showDayBorder = true,
        showSelectedDayBorder = showSelectedDayBorder,
        cellSizePx = cellSizePx,
        gapPx = gapPx,
        weekdayHeightPx = weekdayHeightPx,
        fontSizePx = fontSizePx,
        borderRadiusPx = borderRadiusPx
    )
}
